"use client"
import endPoints from "@/lib/apiEndPoints"
import link from '@/lib/localEndPoints'
import { useFetchSWR, useFetchSWRInfinite } from "@/lib/swrConfig"
import Link from "next/link"

import { EventsCard } from "@/components/pages/events/EventsCard"
import { RecentEvents } from "@/components/pages/events/RecentEvents"
import { BannerCarousel } from "@/components/reusebale-components/bannerCarousel"
import { CallToAction } from "@/components/reusebale-components/CallToAction"
import HeaderTitle from "@/components/reusebale-components/HeaderTitle"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"

import { bannerEnum as be } from "@/lib/utils"
import { BannerData } from "@/types/banner"
import { EventData } from "@/types/event"

const QUERY_LIMIT = 8

export default function Events( ) {
  // Banner
  const { data: bannerData, isLoading} = useFetchSWR(endPoints.BANNER(be.EVENT))
  const banner: BannerData =
    bannerData?.status === 200 ? bannerData.data : {}

  // Infinite Events Fetch
  const getKey = (pageIndex: number, previousPageData: any) => {
    if (previousPageData && !previousPageData.data.length) return null
    return endPoints.GET_EVENTS(pageIndex + 1, QUERY_LIMIT, 0) 
  }

  const { data, size, setSize, isValidating } = useFetchSWRInfinite(getKey)

  const events: EventData[] = data ? data.flatMap((page) => page.data) : []
  const isReachingEnd =
    data && data[data.length - 1]?.data.length < QUERY_LIMIT

  // Recent Events = first 5 only
  const recentEvents = events.slice(0, 5)

  return (
    <main>
      {/* Banner */}
      {
        isLoading ? (
            <div className="h-screen flex items-center justify-center">
            <Spinner />
            </div>
        ) : (
            <BannerCarousel
            images={banner?.media?.map((media) => media.url)}
            title={banner?.title}
            description={banner?.details}
            />
        )
      }

      <div className="container mt-6 md:mt-10 lg:mt-16">
        {/* Recent events */}
        
        { recentEvents.length > 0 && (
          <div>
            <HeaderTitle title="Most Recent Events" url="" />
            <RecentEvents events={recentEvents} />
          </div>
        )}

        { recentEvents.length > 0 && (
          <Separator className="my-6 md:my-10 lg:my-16" />
        )}

        {/* All events */}
        {
          events.length > 4 && (
            <div className="my-10">
              <HeaderTitle title="Find Our All Events" url="" />

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
                {events.slice(5).map((event: EventData) => (
                  <Link rel="preload"  prefetch href={`${link.EVENT}/${event.slug}`} key={event.id}>
                    <div className="h-40 md:h-64 w-full">
                      <EventsCard {...event} />
                    </div>
                  </Link>
                ))}
              </div>

              {/* See More Button */}
              {!isReachingEnd && (
                <div className="mt-8 text-center">
                  <Button
                    onClick={() => setSize(size + 1)}
                    disabled={isValidating}
                    className="bg-primary text-white rounded-full px-8 py-3 cursor-pointer hover:bg-prime-hover transition-colors"
                    size="lg"
                  >
                    {isValidating ? <Spinner /> : "See More"}
                  </Button>
                </div>
              )}
            </div>
        )}
        { events.length > 4 && (<Separator className="my-6 md:my-10 lg:my-16 " />)}

        <div className="mb-6 md:mb-10 lg:mb-16">
          <CallToAction
            title="Join Us in Making a Difference"
            description="Your support can create lasting change in our communities. Get involved and make an impact today!"
            buttonText="Contact"
          />
        </div>
      </div>
    </main>
  )
}
